// In-memory mock store. Single module-level singleton.
export interface MockLineItem {
  group: string
  description: string
  quantity: string
  unit_price: string
  unit?: string
  line_total: string
  line_tax?: string | null
  vat_rate: string
  discount_percent?: string
  discount_amount?: string
}

export interface MockAnalyticPart {
  acc: string
  level1?: string
  level1_name?: string
  level2?: string
  level2_name?: string
  level3?: string
  level3_name?: string
}

export interface MockErpLineItem {
  line_item_group: string
  deal_name: number
  acc_assignment: number
  analytic_parts?: MockAnalyticPart[]
  without_account_assignment?: boolean
}

export interface MockExtractedFields {
  vendor_name?: string
  vendor_vat?: string
  vendor_tax_id?: string
  vendor_address?: string
  vendor_country?: string
  customer_name?: string
  customer_address?: string
  customer_tax_id?: string
  customer_country?: string
  invoice_number?: string
  mirage_invoice_number?: string
  invoice_date?: string
  due_date?: string
  payment_terms?: string
  message_type?: string
  additional_remarks?: string
  currency?: string
  subtotal?: number
  tax_amount?: number
  total_amount?: number
  vat_rate?: number
  description?: string
}

export interface MockInvoice {
  id: string
  original_filename: string
  file_path: string
  file_size: number
  mime_type: string
  status: 'uploading' | 'uploaded' | 'processing' | 'completed' | 'failed' | 'review_required' | 'reviewed' | 'exported'
  overall_confidence?: number
  invoice_type?: string
  invoice_date?: string
  uploaded_at: string
  processed_at?: string
  error_message?: string
  extracted: MockExtractedFields
  line_items?: MockLineItem[]
  erp_line_items?: MockErpLineItem[]
  mirage_counterparty_id?: number
  counterparty?: {
    id: number
    name: string
    code?: string
    vat_code?: string
    address?: string
    country?: string
  }
  deal_name_id?: number
  account_assignment_id?: number
  user_uploaded?: boolean
  blob_url?: string
}

export interface MockBankTransaction {
  id: string
  booking_ts: string
  description: string
  counterparty_name: string
  reference: string
  amount: number
  currency: string
  matched: boolean
  match_invoice_ids?: string[]
}

export type MockMatchState =
  | 'suggested'
  | 'auto_confirmed'
  | 'confirmed'
  | 'committed'
  | 'rejected'
  | 'reversed'

export interface MockCounterpart {
  id: string
  counterpart_kind: 'mirage_open_item' | 'gl_account_post' | 'bank_account_transfer'
  dt_account_id: number
  ct_account_id: number
  dt_apx_id?: number
  ct_apx_id?: number
  amount: string
  note?: string
  doc_nom?: string
  doc_date?: string
}

export interface MockMatch {
  id: string
  transaction_id: string
  state: MockMatchState
  match_kind: 'invoice_payment' | 'gl_post' | 'transfer' | 'split' | 'manual_link' | 'unmatched'
  mirage_bank_account_id: number
  agent_confidence?: number
  agent_reasoning?: string
  counterparts: MockCounterpart[]
  confirmed_at?: string
  committed_at?: string
  created_at: string
}

export interface MockClient {
  id: string
  name: string
  status: string
  mssql_client_id?: string
  user_id: string
  created_at: string
  updated_at: string
  vat_code?: string
  eik?: string
  address?: string
  mol?: string
  iban?: string
  bank?: string
  activity?: string
}

export interface MockUser {
  id: string
  email: string
  full_name: string
  is_active: boolean
  is_superuser: boolean
  role: 'admin' | 'regular' | 'super_admin'
  status: 'active' | 'pending' | 'disabled'
  learning_preference: 'always_ask' | 'always_learn' | 'never_learn'
  created_at: string
  updated_at: string
  mirage_username: string
  mirage_user_id?: number | null
}

import { invoiceFixtures } from './fixtures/invoices'
import { bankTransactionsFixture } from './fixtures/bankTransactions'

class MockStore {
  invoices: MockInvoice[] = [...invoiceFixtures]
  bankTransactions: MockBankTransaction[] = [...bankTransactionsFixture]
  matches: MockMatch[] = []
  clients: MockClient[] = [
    {
      id: 'client-default',
      name: 'Гремлинс Лабс ООД',
      status: 'active',
      mssql_client_id: '4',
      user_id: 'mock-user',
      created_at: '2026-01-15T09:00:00Z',
      updated_at: '2026-04-30T10:00:00Z',
      vat_code: 'BG207654321',
      eik: '207654321',
      address: 'гр. София, ж.к. Изток, ул. "Яворов" 73, ет. 11, ап. 24',
      mol: 'Иван Иванов',
      iban: 'BG80BNBG96611020345678',
      bank: 'ДСК Банк',
      activity: 'Software development',
    },
  ]
  currentUser: MockUser = {
    id: 'mock-user',
    email: 'demo@superstate.one',
    full_name: 'Demo User',
    is_active: true,
    is_superuser: true,
    role: 'admin',
    status: 'active',
    learning_preference: 'always_ask',
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
    mirage_username: 'demo',
    mirage_user_id: 1,
  }
  conversations: Array<{ id: string; title: string; created_at: string; updated_at: string; messages: unknown[] }> = []

  addInvoice(invoice: MockInvoice) {
    this.invoices.unshift(invoice)
  }

  updateInvoice(id: string, patch: Partial<MockInvoice>) {
    const idx = this.invoices.findIndex((i) => i.id === id)
    if (idx >= 0) {
      this.invoices[idx] = { ...this.invoices[idx], ...patch }
    }
  }

  getInvoice(id: string) {
    return this.invoices.find((i) => i.id === id)
  }

  removeInvoice(id: string) {
    this.invoices = this.invoices.filter((i) => i.id !== id)
  }
}

export const mockStore = new MockStore()
